import { type MediaCell, parseMediaCell } from '@jbroll/rowboat-client';
import type { Graph } from '@/schema/estate';
import { getIdentifyQueue, type IdentifyQueue } from './identifyQueue';

// The blob client puts the hash into a request path sent with the viewer's token, and any
// collaborator can write the cell, so only a SHA-256 hex digest counts as media.
const MEDIA_HASH = /^[0-9a-f]{64}$/;

// The local store keeps json cells as JSON text (rowboat's create and update stringify them),
// so a row read back holds a string where the write passed an object.
export function readMedia(cell: unknown): MediaCell | undefined {
  const media = typeof cell === 'string' ? parseJsonMedia(cell) : parseMediaCell(cell);
  return media && MEDIA_HASH.test(media.hash) ? media : undefined;
}

function parseJsonMedia(text: string): MediaCell | undefined {
  try {
    return parseMediaCell(JSON.parse(text));
  } catch {
    return undefined;
  }
}

export async function createPhotoRow(
  g: Graph,
  opts: { itemId: string; groupId: string; author: string; role: string; sortOrder: number },
): Promise<string> {
  const id = crypto.randomUUID();
  const now = Date.now();
  await g.item_photo.create({
    id,
    owner_group_id: opts.groupId,
    item_id: opts.itemId,
    storage_key: null,
    thumbnail_key: null,
    role: opts.role,
    sort_order: opts.sortOrder,
    created_by: opts.author,
    created_at: now,
    archived: false,
  });
  return id;
}

export async function attachPhotoKeys(
  g: Graph,
  photoId: string,
  cells: { storage: MediaCell; thumbnail: MediaCell | null },
): Promise<void> {
  await g.item_photo.update(photoId, {
    storage_key: cells.storage,
    thumbnail_key: cells.thumbnail,
  });
}

export async function archivePhoto(g: Graph, photoId: string): Promise<void> {
  await g.item_photo.update(photoId, { archived: true });
}

/** Archives a photo the user removed and re-identifies its item from the photos left. */
export async function deletePhoto(
  g: Graph,
  photoId: string,
  opts: { identifyQueue?: Pick<IdentifyQueue, 'touch'> } = {},
): Promise<void> {
  const itemId = g.item_photo(photoId)?.$data.item_id;
  await archivePhoto(g, photoId);
  // Identification is secondary: a queue hiccup must not report the delete as failed.
  if (itemId)
    await (opts.identifyQueue ?? getIdentifyQueue())
      .touch(itemId)
      .catch((err) => console.warn('identify touch failed', itemId, err));
}

export function photosForItem(g: Graph, itemId: string) {
  return g.item_photo
    .all()
    .filter((p) => p.item_id === itemId && !p.archived)
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** Past every photo the item ever had, so a new shot never ties with a deleted one's slot. */
export function nextSortOrder(g: Graph, itemId: string): number {
  return g.item_photo
    .all()
    .filter((p) => p.item_id === itemId)
    .reduce((next, p) => Math.max(next, p.sort_order + 1), 0);
}

/** The photo's thumbnail hash; undefined when it has none, or repeats the original's (older rows). */
export function thumbHash(photo: { storage_key: unknown; thumbnail_key: unknown } | undefined) {
  const thumb = readMedia(photo?.thumbnail_key)?.hash;
  return thumb === readMedia(photo?.storage_key)?.hash ? undefined : thumb;
}

export function primaryPhotoFor(g: Graph, itemId: string) {
  return photosForItem(g, itemId)[0];
}
